import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { be1Fetch } from '../../../../config/api';
import { useCapabilities, shellBlockedKey } from '../../../../services/capabilities';

interface Props {
  cfg: Record<string, any>;
  patch: (p: Record<string, any>) => void;
}

/**
 * 외부명령어 노드의 본문 — 두 형태 (서버 services/shell_policy)
 *   명령 직접 입력  자유 명령. 서버 설정(OPENFLOW_SHELL)의 권한자만.
 *   등록 스크립트   관리자가 승인 폴더에 둔 스크립트를 고르고 인자를 적는다. 셸을 거치지 않는다.
 * 쓸 수 없는 형태는 고를 수 없게 하고 이유를 보인다. 판정은 저장·배포·실행 때 서버가 다시 한다.
 */
const ShellCommandSection: React.FC<Props> = ({ cfg, patch }) => {
  const { t } = useTranslation();
  const caps = useCapabilities();
  const blocked = shellBlockedKey(caps);
  const canCommand = !caps || caps.shell.allowed;
  const canScript = !!caps && caps.shell.scripts;
  const isScript = cfg.mode === 'script';
  const [scripts, setScripts] = useState<string[] | null>(null);

  // 직접 입력을 못 쓰고 등록 스크립트만 되는 사람이 새 노드를 열면 등록 스크립트로 시작한다
  useEffect(() => {
    if (caps && !caps.shell.allowed && caps.shell.scripts && !isScript && !(cfg.command || '').trim()) patch({ mode: 'script' });
  }, [caps, isScript, cfg.command, patch]);

  useEffect(() => {
    if (!isScript) return;
    let alive = true;
    be1Fetch('/api/shell/scripts').then((r) => (r.ok ? r.json() : { items: [] })).then((j) => { if (alive) setScripts(j.items || []); })
      .catch(() => { if (alive) setScripts([]); });
    return () => { alive = false; };
  }, [isScript]);

  const args: string[] = Array.isArray(cfg.args) ? cfg.args : [];
  const known = scripts || [];

  return (
    <>
      {blocked && (
        <div className="mb-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[0.6875rem] text-amber-800">{t(blocked)} {t('property:exec.shellBlockedTail')}</div>
      )}
      <div className="flex items-center gap-4 mb-1.5 text-xs text-gray-700">
        <label className={`flex items-center gap-1 ${canCommand || !isScript ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
          title={canCommand ? undefined : t('property:exec.shellCommandOff')}>
          <input type="radio" checked={!isScript} disabled={!canCommand && isScript} onChange={() => patch({ mode: 'command' })} />
          {t('property:exec.shellModeCommand')}
        </label>
        <label className={`flex items-center gap-1 ${canScript || isScript ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
          title={canScript ? undefined : t('property:exec.shellScriptOff')}>
          <input type="radio" checked={isScript} disabled={!canScript && !isScript} onChange={() => patch({ mode: 'script' })} />
          {t('property:exec.shellModeScript')}
        </label>
      </div>

      {isScript ? (
        <>
          <select className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-white mb-1" value={cfg.script || ''}
            onChange={(e) => patch({ script: e.target.value })}>
            <option value="">{t('property:exec.chooseScript')}</option>
            {/* 목록에 없어진 스크립트도 보여 준다 — 무엇을 골라 두었는지 알 수 있게 */}
            {cfg.script && !known.includes(cfg.script) && <option value={cfg.script}>{cfg.script} {t('property:exec.scriptMissing')}</option>}
            {known.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {scripts && scripts.length === 0 && <div className="text-[0.6875rem] text-amber-700 mb-1">{t('property:exec.noScripts')}</div>}
          <label className="block text-xs text-gray-600 mb-0.5">{t('property:exec.scriptArgs')}</label>
          <textarea className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-white" rows={3}
            value={args.join('\n')} placeholder={t('property:exec.scriptArgsPlaceholder')}
            onChange={(e) => patch({ args: e.target.value.split('\n') })} />
          <div className="text-[0.6875rem] text-gray-500 mt-1 mb-1.5">{t('property:exec.scriptHint')} <code>{'${name}'}</code> {t('property:exec.scriptHintTail')}</div>
        </>
      ) : (
        <>
          <textarea className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-white" rows={6} value={cfg.command || ''} onChange={(e) => patch({ command: e.target.value })}
            placeholder={t('property:exec.shellPlaceholder')} />
          <div className="text-[0.6875rem] text-gray-500 mt-1 mb-1.5">{t('property:exec.shellHint1')} <code>{'${name}'}</code> {t('property:exec.shellHint2')} <code>OF_name</code>{t('property:exec.shellHint3')}</div>
          {/* 실행 때 넣는 값은 명령문에 직접 넣지 않는다 — 특수문자가 있으면 서버가 거절한다(shell.unsafeValue) */}
          <div className="text-[0.6875rem] text-gray-500 mb-1.5">{t('property:exec.shellRunValueHint')} <code>$OF_name</code> / <code>%OF_name%</code></div>
        </>
      )}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1 text-xs text-gray-700 flex-1">{t('property:exec.cwd')}<input className="flex-1 border border-gray-300 rounded px-1 py-0.5 bg-white font-mono" value={cfg.cwd || ''} placeholder={t('property:exec.default')} onChange={(e) => patch({ cwd: e.target.value })} /></label>
        <label className="flex items-center gap-1 text-xs text-gray-700">{t('property:exec.timeout')}<input type="number" className="w-20 border border-gray-300 rounded px-1 py-0.5 bg-white" value={cfg.timeoutSecs ?? 3600} onChange={(e) => patch({ timeoutSecs: Number(e.target.value) })} /></label>
      </div>
    </>
  );
};

export default ShellCommandSection;
